import React from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import {
  FaHeart,
  FaGraduationCap,
  FaHandsHelping,
  FaTree,
  FaLeaf,
  FaUsers,
  FaShieldAlt,
  FaChartLine,
  FaCertificate,
  FaSeedling,
  FaHandshake,
  FaBalanceScale,
  FaRecycle,
  FaBolt,
  FaWater,
  FaBoxOpen,
  FaUserShield,
  FaClipboardCheck,
  FaFileAlt,
  FaTractor,
  FaDollarSign,
  FaLightbulb,
  FaAppleAlt,
  FaTruck,
  FaCloudSun,
  FaBook,
} from "react-icons/fa";

const CSR = () => {
  const { t } = useTranslation();

  const pillarIcons = [
    <FaHandshake />,
    <FaTree />,
    <FaUserShield />,
    <FaHandsHelping />,
    <FaShieldAlt />,
  ];

  const pillarColors = [
    "from-green-500 to-emerald-600",
    "from-blue-500 to-cyan-600",
    "from-purple-500 to-pink-600",
    "from-amber-500 to-orange-600",
    "from-indigo-500 to-purple-600",
  ];

  const pillarItemIcons = [
    [<FaUsers />, <FaDollarSign />, <FaGraduationCap />, <FaClipboardCheck />],
    [<FaLeaf />, <FaSeedling />, <FaWater />, <FaRecycle />],
    [<FaShieldAlt />, <FaBalanceScale />, <FaLightbulb />, <FaHeart />],
    [<FaBook />, <FaHeart />, <FaHandsHelping />],
    [<FaFileAlt />, <FaShieldAlt />, <FaClipboardCheck />, <FaChartLine />],
  ];

  const climateIcons = [<FaCloudSun />, <FaBolt />, <FaTruck />];

  const certificationIcons = [
    <FaLeaf />,
    <FaSeedling />,
    <FaCertificate />,
    <FaHandshake />,
  ];

  const impactIcons = [
    <FaTractor />,
    <FaHandshake />,
    <FaBolt />,
    <FaRecycle />,
    <FaShieldAlt />,
    <FaUsers />,
  ];

  const farmerSupportIcons = [
    <FaUsers />,
    <FaClipboardCheck />,
    <FaDollarSign />,
  ];

  const productDesignIcons = [<FaLeaf />, <FaAppleAlt />, <FaBoxOpen />];

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-blue-50 to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      {/* Spacer for navbar */}
      <div className="h-4"></div>

      {/* Hero Section */}
      <section className="container mx-auto px-4 py-16 md:py-24">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="max-w-6xl mx-auto text-center"
        >
          <motion.div
            initial={{ scale: 0.9 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.5 }}
            className="inline-block mb-6"
          >
            <span className="px-6 py-2 bg-gradient-to-r from-green-600 to-blue-600 text-white rounded-full text-sm font-semibold shadow-lg">
              {t("csr.hero.badge")}
            </span>
          </motion.div>

          <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-8 leading-tight">
            <span className="text-gray-900 dark:text-white">
              {t("csr.hero.title.part1")}
            </span>
            <span className="text-green-600 dark:text-green-500">
              {t("csr.hero.title.part2")}
            </span>
          </h1>

          <p className="text-lg md:text-xl text-gray-700 dark:text-gray-300 leading-relaxed max-w-5xl mx-auto">
            {t("csr.hero.description")}
          </p>
        </motion.div>
      </section>

      {/* Our Pillars */}
      <section className="container mx-auto px-4 py-16">
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="max-w-7xl mx-auto"
        >
          <h2 className="text-3xl md:text-5xl font-bold text-center text-gray-900 dark:text-white mb-12">
            {t("csr.pillarsSection.title")}
          </h2>

          {/* Pillar Cards */}
          <div className="space-y-8">
            {t("csr.pillars", { returnObjects: true }).map((pillar, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl overflow-hidden"
              >
                {/* Pillar Header */}
                <div
                  className={`bg-gradient-to-r ${pillarColors[index]} p-6 md:p-8 text-white`}
                >
                  <div className="flex items-center gap-4">
                    <div className="text-5xl">{pillar.emoji}</div>
                    <div className="flex-1">
                      <h3 className="text-2xl md:text-3xl font-bold">
                        {pillar.title}
                      </h3>
                    </div>
                    <div className="text-4xl opacity-80">
                      {pillarIcons[index]}
                    </div>
                  </div>
                </div>

                {/* Pillar Content */}
                <div className="p-6 md:p-8">
                  <div className="grid md:grid-cols-2 gap-6">
                    {pillar.items.map((item, i) => (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: i * 0.05 }}
                        className="flex gap-4"
                      >
                        <div
                          className={`text-3xl text-transparent bg-gradient-to-r ${pillarColors[index]} bg-clip-text flex-shrink-0`}
                        >
                          {pillarItemIcons[index][i]}
                        </div>
                        <div>
                          <h4 className="font-bold text-gray-900 dark:text-white mb-2">
                            {item.title}
                          </h4>
                          <p className="text-gray-600 dark:text-gray-400 text-sm">
                            {item.desc}
                          </p>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Climate & Footprint */}
      <section className="bg-gradient-to-r from-blue-600 to-cyan-600 py-16 text-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="max-w-6xl mx-auto"
          >
            <h2 className="text-3xl md:text-5xl font-bold text-center mb-12">
              {t("csr.climate.title")}
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              {t("csr.climate.actions", { returnObjects: true }).map(
                (action, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-white/10 backdrop-blur-sm rounded-xl p-6 hover:bg-white/20 transition-all duration-300"
                  >
                    <div className="text-5xl mb-4">{climateIcons[index]}</div>
                    <h3 className="text-xl font-bold mb-3">{action.title}</h3>
                    <p className="text-blue-100">{action.desc}</p>
                  </motion.div>
                )
              )}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Certifications */}
      <section className="container mx-auto px-4 py-16">
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="max-w-6xl mx-auto"
        >
          <h2 className="text-3xl md:text-5xl font-bold text-center text-gray-900 dark:text-white mb-12">
            {t("csr.certifications.title")}
          </h2>
          <div className="grid md:grid-cols-4 gap-6">
            {t("csr.certifications.list", { returnObjects: true }).map(
              (cert, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 text-center hover:shadow-2xl transition-all duration-300"
                >
                  <div className="text-5xl text-green-600 mb-4 flex justify-center">
                    {certificationIcons[index]}
                  </div>
                  <h3 className="font-bold text-gray-900 dark:text-white">
                    {cert}
                  </h3>
                </motion.div>
              )
            )}
          </div>
        </motion.div>
      </section>

      {/* Impact Highlights */}
      <section className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-gray-900 dark:to-gray-800 py-16">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="max-w-7xl mx-auto"
          >
            <h2 className="text-3xl md:text-5xl font-bold text-center text-gray-900 dark:text-white mb-12">
              {t("csr.impact.title")}
            </h2>
            <div className="grid md:grid-cols-3 lg:grid-cols-6 gap-6">
              {t("csr.impact.metrics", { returnObjects: true }).map(
                (metric, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.05 }}
                    className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 text-center hover:shadow-2xl transition-all duration-300"
                  >
                    <div className="text-4xl text-purple-600 mb-3 flex justify-center">
                      {impactIcons[index]}
                    </div>
                    <div className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                      {metric.value}
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      {metric.label}
                    </div>
                  </motion.div>
                )
              )}
            </div>
          </motion.div>
        </div>
      </section>

      {/* How We Work with Farmers */}
      <section className="container mx-auto px-4 py-16">
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="max-w-6xl mx-auto"
        >
          <h2 className="text-3xl md:text-5xl font-bold text-center text-gray-900 dark:text-white mb-12">
            {t("csr.farmers.title")}
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {t("csr.farmers.support", { returnObjects: true }).map(
              (support, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-gray-800 dark:to-gray-700 rounded-xl shadow-lg p-8 hover:shadow-2xl transition-all duration-300"
                >
                  <div className="text-5xl text-green-600 mb-4">
                    {farmerSupportIcons[index]}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                    {support.title}
                  </h3>
                  <p className="text-gray-700 dark:text-gray-300">
                    {support.desc}
                  </p>
                </motion.div>
              )
            )}
          </div>
        </motion.div>
      </section>

      {/* Responsible Product Design */}
      <section className="bg-gradient-to-r from-amber-600 to-orange-600 py-16 text-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="max-w-6xl mx-auto"
          >
            <h2 className="text-3xl md:text-5xl font-bold text-center mb-12">
              {t("csr.productDesign.title")}
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              {t("csr.productDesign.items", { returnObjects: true }).map(
                (design, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-white/10 backdrop-blur-sm rounded-xl p-6 hover:bg-white/20 transition-all duration-300"
                  >
                    <div className="text-5xl mb-4">
                      {productDesignIcons[index]}
                    </div>
                    <h3 className="text-xl font-bold mb-3">{design.title}</h3>
                    <p className="text-amber-100">{design.desc}</p>
                  </motion.div>
                )
              )}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Bottom Spacer */}
      <div className="h-16"></div>
    </div>
  );
};

export default CSR;
